import { useState } from 'react';
import { useSchemaDesignerStore, ColumnType, Column } from '@/stores/schema-designer-store';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { RiAddLine, RiInformationLine } from '@remixicon/react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface AddColumnDialogProps {
  tableId: string;
  tableName: string;
  trigger?: React.ReactNode;
}

const COLUMN_TYPES: { value: ColumnType; label: string; description: string }[] = [
  { value: 'varchar', label: 'VARCHAR', description: 'Variable-length string' },
  { value: 'text', label: 'TEXT', description: 'Long text' },
  { value: 'integer', label: 'INTEGER', description: 'Whole number' },
  { value: 'bigint', label: 'BIGINT', description: 'Large whole number' },
  { value: 'float', label: 'FLOAT', description: 'Floating point number' },
  { value: 'double', label: 'DOUBLE', description: 'Double precision number' },
  { value: 'decimal', label: 'DECIMAL', description: 'Exact decimal number' },
  { value: 'boolean', label: 'BOOLEAN', description: 'True/false value' },
  { value: 'date', label: 'DATE', description: 'Date only' },
  { value: 'datetime', label: 'DATETIME', description: 'Date and time' },
  { value: 'timestamp', label: 'TIMESTAMP', description: 'Timestamp with timezone' },
  { value: 'time', label: 'TIME', description: 'Time only' },
  { value: 'uuid', label: 'UUID', description: 'Unique identifier' },
  { value: 'json', label: 'JSON', description: 'JSON data' },
  { value: 'jsonb', label: 'JSONB', description: 'Binary JSON (PostgreSQL)' },
  { value: 'enum', label: 'ENUM', description: 'Enumerated type' },
  { value: 'array', label: 'ARRAY', description: 'Array type (PostgreSQL)' },
];

export const AddColumnDialog = ({ tableId, tableName, trigger }: AddColumnDialogProps) => {
  const [open, setOpen] = useState(false);
  const { addColumn, nodes } = useSchemaDesignerStore();

  // Form state
  const [columnName, setColumnName] = useState('');
  const [columnType, setColumnType] = useState<ColumnType>('varchar');
  const [length, setLength] = useState('');
  const [precision, setPrecision] = useState('');
  const [scale, setScale] = useState('');
  const [nullable, setNullable] = useState(true);
  const [primaryKey, setPrimaryKey] = useState(false);
  const [unique, setUnique] = useState(false);
  const [autoIncrement, setAutoIncrement] = useState(false);
  const [defaultValue, setDefaultValue] = useState('');
  const [comment, setComment] = useState('');

  const resetForm = () => {
    setColumnName('');
    setColumnType('varchar');
    setLength('');
    setPrecision('');
    setScale('');
    setNullable(true);
    setPrimaryKey(false);
    setUnique(false);
    setAutoIncrement(false);
    setDefaultValue('');
    setComment('');
  };

  const handleAddColumn = () => {
    const trimmedName = columnName.trim();

    if (!trimmedName) {
      toast.error('Column name is required');
      return;
    }

    // Validate column name format
    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(trimmedName)) {
      toast.error('Column name must start with a letter or underscore and contain only letters, numbers, and underscores');
      return;
    }

    // Check if column name already exists in this table
    const table = nodes.find((node) => node.id === tableId);
    if (table?.data.columns.some((col) => col.name.toLowerCase() === trimmedName.toLowerCase())) {
      toast.error(`Column "${trimmedName}" already exists in table "${tableName}"`);
      return;
    }

    // Validate primary key - only one primary key allowed per table
    if (primaryKey && table?.data.columns.some((col) => col.primaryKey)) {
      toast.error('This table already has a primary key column. Only one primary key is allowed per table.');
      return;
    }

    // Validate length for varchar
    if (columnType === 'varchar' && length && (parseInt(length) <= 0 || parseInt(length) > 65535)) {
      toast.error('VARCHAR length must be between 1 and 65535');
      return;
    }

    // Validate precision/scale for decimal
    if (columnType === 'decimal') {
      if (precision && (parseInt(precision) <= 0 || parseInt(precision) > 65)) {
        toast.error('Decimal precision must be between 1 and 65');
        return;
      }
      if (scale && (parseInt(scale) < 0 || parseInt(scale) > 30)) {
        toast.error('Decimal scale must be between 0 and 30');
        return;
      }
    }

    const newColumn: Column = {
      id: `column-${Date.now()}-${Math.random()}`,
      name: trimmedName,
      type: columnType,
      length: length ? parseInt(length) : undefined,
      precision: precision ? parseInt(precision) : undefined,
      scale: scale ? parseInt(scale) : undefined,
      nullable,
      primaryKey,
      unique,
      autoIncrement,
      defaultValue: defaultValue.trim() || undefined,
      comment: comment.trim() || undefined,
    };

    addColumn(tableId, newColumn);
    toast.success(`Column "${trimmedName}" added to "${tableName}"`);

    resetForm();
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && columnName.trim()) {
      e.preventDefault();
      handleAddColumn();
    }
  };

  // Helper to determine which fields to show based on type
  const showLengthField = columnType === 'varchar';
  const showPrecisionScaleFields = columnType === 'decimal';
  const showAutoIncrement = ['integer', 'bigint'].includes(columnType);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className="gap-2">
            <RiAddLine className="h-4 w-4" />
            Add Column
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[580px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RiAddLine className="h-5 w-5 text-primary" />
            Add Column to "{tableName}"
          </DialogTitle>
          <DialogDescription>
            Define a new column with its data type, constraints, and properties.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Column Name */}
          <div className="space-y-2">
            <Label htmlFor="columnName" className="text-sm font-medium">
              Column Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="columnName"
              placeholder="e.g., user_id, email, created_at"
              value={columnName}
              onChange={(e) => setColumnName(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              className="font-mono"
            />
          </div>

          {/* Column Type */}
          <div className="space-y-2">
            <Label htmlFor="columnType" className="text-sm font-medium">
              Data Type <span className="text-destructive">*</span>
            </Label>
            <Select value={columnType} onValueChange={(value) => setColumnType(value as ColumnType)}>
              <SelectTrigger id="columnType" className="font-mono">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COLUMN_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value} className="font-mono">
                    <div className="flex flex-col items-start">
                      <span className="font-semibold">{type.label}</span>
                      <span className="text-xs text-muted-foreground">{type.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Length Field (for VARCHAR) */}
          {showLengthField && (
            <div className="space-y-2">
              <Label htmlFor="length" className="text-sm font-medium">
                Length
              </Label>
              <Input
                id="length"
                type="number"
                placeholder="e.g., 255"
                value={length}
                onChange={(e) => setLength(e.target.value)}
                min="1"
                max="65535"
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Maximum number of characters (default: 255)
              </p>
            </div>
          )}

          {/* Precision & Scale (for DECIMAL) */}
          {showPrecisionScaleFields && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="precision" className="text-sm font-medium">
                  Precision
                </Label>
                <Input
                  id="precision"
                  type="number"
                  placeholder="e.g., 10"
                  value={precision}
                  onChange={(e) => setPrecision(e.target.value)}
                  min="1"
                  max="65"
                  className="font-mono"
                />
                <p className="text-xs text-muted-foreground">Total digits</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="scale" className="text-sm font-medium">
                  Scale
                </Label>
                <Input
                  id="scale"
                  type="number"
                  placeholder="e.g., 2"
                  value={scale}
                  onChange={(e) => setScale(e.target.value)}
                  min="0"
                  max="30"
                  className="font-mono"
                />
                <p className="text-xs text-muted-foreground">Decimal places</p>
              </div>
            </div>
          )}

          {/* Constraints */}
          <div className="space-y-3 pt-2">
            <Label className="text-sm font-medium">Constraints</Label>
            <div className="space-y-3 pl-1">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="primaryKey"
                  checked={primaryKey}
                  onCheckedChange={(checked) => {
                    setPrimaryKey(checked as boolean);
                    if (checked) {
                      setNullable(false);
                      setUnique(true);
                    }
                  }}
                />
                <div className="space-y-1">
                  <Label htmlFor="primaryKey" className="text-sm font-medium cursor-pointer">
                    Primary Key
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Uniquely identifies each row (automatically sets NOT NULL and UNIQUE)
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="unique"
                  checked={unique}
                  onCheckedChange={(checked) => setUnique(checked as boolean)}
                  disabled={primaryKey}
                />
                <div className="space-y-1">
                  <Label htmlFor="unique" className={cn("text-sm font-medium cursor-pointer", primaryKey && "text-muted-foreground")}>
                    Unique
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Ensures all values in this column are different
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="nullable"
                  checked={nullable}
                  onCheckedChange={(checked) => setNullable(checked as boolean)}
                  disabled={primaryKey}
                />
                <div className="space-y-1">
                  <Label htmlFor="nullable" className={cn("text-sm font-medium cursor-pointer", primaryKey && "text-muted-foreground")}>
                    Nullable
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Allow NULL values in this column
                  </p>
                </div>
              </div>

              {showAutoIncrement && (
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="autoIncrement"
                    checked={autoIncrement}
                    onCheckedChange={(checked) => setAutoIncrement(checked as boolean)}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="autoIncrement" className="text-sm font-medium cursor-pointer">
                      Auto Increment
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Automatically generate sequential values
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Default Value */}
          <div className="space-y-2">
            <Label htmlFor="defaultValue" className="text-sm font-medium flex items-center gap-2">
              Default Value
              <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Input
              id="defaultValue"
              placeholder="e.g., CURRENT_TIMESTAMP, 0, 'active'"
              value={defaultValue}
              onChange={(e) => setDefaultValue(e.target.value)}
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground flex items-start gap-1">
              <RiInformationLine className="h-3 w-3 mt-0.5 flex-shrink-0" />
              Use CURRENT_TIMESTAMP for timestamps, wrap strings in quotes
            </p>
          </div>

          {/* Comment */}
          <div className="space-y-2">
            <Label htmlFor="comment" className="text-sm font-medium">
              Comment <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              id="comment"
              placeholder="Brief description of this column..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={2}
              className="resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleAddColumn} disabled={!columnName.trim()} className="gap-2">
            <RiAddLine className="h-4 w-4" />
            Add Column
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
